import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { Typography } from "@mui/material";
import { useAlternativeRegionNames } from "./data/Settings/useAlternativeRegionNames";
import { Region } from "./shared/interfaces/Region";
import { Autocomplete, TextField } from "@mui/material";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import React from "react";

interface Props {
  customerId: number | undefined;
  selectedCompanyName?: string;
  onCompanyChange: (company: string, companyId?: number) => void;
  showLabel?: boolean;
  showClear?: boolean;
  className?: string;
  clearAuto?: boolean;
  companies: Region[] | undefined;
  disabled?: boolean;
  width?: string;
}

export const CompanyDropdownWithoutLoader = React.forwardRef<typeof Autocomplete, Props>((props, ref) => {
  const { t } = useTranslation();
  const {
    customerId,
    selectedCompanyName,
    onCompanyChange,
    showLabel,
    showClear,
    clearAuto,
    className,
    companies,
    disabled,
    width,
  } = props;

  const altRegionNames = useAlternativeRegionNames(customerId);

  function handleChange(_e: React.SyntheticEvent, value: Region | null) {
    onCompanyChange(value?.name ?? "", value?.id ?? undefined);
  }

  const companyLabelTemplate = (
    <Col xs={"auto"} className="d-flex align-items-center">
      <Typography variant="subtitle2" color="textPrimary">
        {altRegionNames.data?.companyAltName ?? t("common.company")}:
      </Typography>
    </Col>
  );

  const selectedCompany = companies?.find((company) => company.name === selectedCompanyName) ?? null;

  return (
    <Row className={showLabel ? "d-flex flex-column ml-1" : ""}>
      {showLabel && companyLabelTemplate}
      <Col {...(clearAuto ? {} : { xs: "auto" })}>
        <Autocomplete
          ref={ref}
          disableClearable={!showClear}
          className={className}
          autoHighlight
          blurOnSelect
          disabled={customerId === undefined || disabled}
          id="companyDropdown"
          value={selectedCompany}
          options={companies ?? []}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("common.select", {
                item: altRegionNames.data?.companyAltName ?? "$t(common.company)",
              })}
            />
          )}
          renderOption={(props, option, { inputValue }) => {
            const matches = match(option.name, inputValue, {
              insideWords: true,
            });
            const parts = parse(option.name, matches);
            return (
              <li {...props}>
                <div>
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      style={{
                        fontWeight: part.highlight ? 700 : 400,
                      }}
                    >
                      {part.text}
                    </span>
                  ))}
                </div>
              </li>
            );
          }}
          size="small"
          sx={{ width: width || 200, bgcolor: "white" }}
          onChange={handleChange}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      </Col>
    </Row>
  );
});
